import React from 'react';
import { Chip } from '@mui/material';
import { AuditSampleResponse } from '../../../utils/api';

interface AuditStatusChipProps {
    /** Active or most-recently-closed audit sample for the project (null when never audited). */
    sample?: AuditSampleResponse | null;
}

const pct = (sample: AuditSampleResponse): string => {
    const f1 = sample.current_f1 ?? sample.terminal_f1;
    return f1 != null ? `${Math.round(f1 * 100)}%` : '-';
};

const auditLabel = (sample: AuditSampleResponse): string =>
    sample.sample_type === 'TRAP' ? 'Trap audit' : 'Audit';

/**
 * Compact summary of the project's most recent audit, shown in the worklist header where the
 * manual kickoff button sits. Reflects the latest audit-sample state: in progress with running
 * score, or a terminal Passed / Failed verdict. Trap audits are labelled distinctly.
 */
const AuditStatusChip: React.FC<AuditStatusChipProps> = ({ sample }) => {
    if (!sample) return null;

    const label = auditLabel(sample);

    if (sample.status === 'IN_PROGRESS') {
        const reviewed = sample.items_reviewed ?? 0;
        return (
            <Chip
                size="small"
                color="info"
                variant="outlined"
                data-testid={`audit-status-chip-${sample.status}`}
                label={`${label} in progress · ${reviewed}/${sample.sample_size} · ${pct(sample)}`}
                sx={{ marginRight: 1 }}
            />
        );
    }

    if (sample.status === 'PASSED') {
        return (
            <Chip
                size="small"
                color="success"
                data-testid={`audit-status-chip-${sample.status}`}
                label={`${label} passed · ${pct(sample)}`}
                sx={{ marginRight: 1 }}
            />
        );
    }

    if (sample.status === 'FAILED') {
        return (
            <Chip
                size="small"
                color="error"
                data-testid={`audit-status-chip-${sample.status}`}
                label={`${label} failed · ${pct(sample)}`}
                sx={{ marginRight: 1 }}
            />
        );
    }

    return null;
};

export default AuditStatusChip;
